import { ChangeDetectionStrategy, Component, ViewEncapsulation } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Observable } from 'rxjs';
import { distinctUntilChanged, map } from 'rxjs/operators';

export type OdooPestana = 'resumen' | 'clientes' | 'comercios' | 'archivos' | 'bitacora' | 'config';

interface PestanaDef { id: OdooPestana; texto: string; icono: string; }

const PESTANAS: PestanaDef[] = [
  { id: 'resumen', texto: 'Resumen', icono: 'grid' },
  { id: 'clientes', texto: 'Clientes', icono: 'users' },
  { id: 'comercios', texto: 'Comercios', icono: 'store' },
  { id: 'archivos', texto: 'Archivos del día', icono: 'file' },
  { id: 'bitacora', texto: 'Bitácora', icono: 'history' },
  { id: 'config', texto: 'Configuración', icono: 'sliders' },
];

/**
 * PANEL ODOO — `console/odoo`.
 * Contenedor con pestañas (?pestana=resumen|clientes|comercios|archivos|bitacora|config). Cada pestaña se crea
 * solo cuando se abre. La capa visual `od-*` (odoo-ui.css) se carga aquí, sin encapsulación, para todos los hijos.
 */
@Component({
  selector: 'app-odoo-panel',
  templateUrl: './odoo-panel.component.html',
  styleUrls: ['./odoo-panel.component.css', '../odoo-ui.css'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
})
export class OdooPanelComponent {
  readonly pestanas = PESTANAS;
  readonly activa$: Observable<OdooPestana> = this.route.queryParamMap.pipe(
    map((q) => (q.get('pestana') === 'maestros' ? 'clientes' : q.get('pestana'))), // enlace viejo
    map((p): OdooPestana => (PESTANAS.some((x) => x.id === p) ? (p as OdooPestana) : 'resumen')),
    distinctUntilChanged(),
  );

  constructor(private route: ActivatedRoute, private router: Router) {}

  irA = (p: OdooPestana): void => {
    this.router.navigate([], { relativeTo: this.route, queryParams: { pestana: p }, queryParamsHandling: 'merge' });
  };

  /** Flechas, Inicio y Fin mueven la selección entre pestañas (patrón WAI-ARIA tabs). */
  teclas(ev: KeyboardEvent, activa: OdooPestana): void {
    const i = PESTANAS.findIndex((p) => p.id === activa);
    let n = -1;
    if (ev.key === 'ArrowRight') n = (i + 1) % PESTANAS.length;
    else if (ev.key === 'ArrowLeft') n = (i - 1 + PESTANAS.length) % PESTANAS.length;
    else if (ev.key === 'Home') n = 0;
    else if (ev.key === 'End') n = PESTANAS.length - 1;
    if (n < 0) return;
    ev.preventDefault();
    this.irA(PESTANAS[n].id);
    setTimeout(() => (document.getElementById('od-tab-' + PESTANAS[n].id) as HTMLElement | null)?.focus());
  }

  porId = (_: number, p: PestanaDef): string => p.id;
}
